//* Libraries imports
import React from "react";
import { FaFileAlt, FaAward, FaEdit } from "react-icons/fa";
import { LuBookText } from "react-icons/lu";

//* Components imports
import Icon from "../../components/common/Icon";
import { getIcon } from "../../component/common/IconMapper";

//* Types imports
import type { FullProcess } from "../../schemas/common/process/process";
import {
  FEATURE_KEYS,
  FEATURE_ROUTES,
  type MainFeatureKey,
} from "../../constants/permissions";

export type ProcessCardItem = {
  id: string;
  title: string;
  description?: string;
  routePath: string;
  icon?: React.ReactNode;
  backgroundColor?: string;
  textColor?: string;
  className?: string;
};

const MAIN_FEATURE_ICON_MAP: Record<MainFeatureKey, any> = {
  [FEATURE_KEYS.EXAM]: FaAward,
  [FEATURE_KEYS.ESSAY]: FaFileAlt,
  [FEATURE_KEYS.RESOURCE]: FaEdit,
  [FEATURE_KEYS.EDITAL]: FaFileAlt,
  [FEATURE_KEYS.INSCRICAO]: LuBookText,
} as const;

const MAIN_FEATURE_COLOR_MAP: Record<
  MainFeatureKey,
  { backgroundColor: string; textColor: string }
> = {
  [FEATURE_KEYS.EXAM]: { backgroundColor: "#e8f5e9", textColor: "#2e7d32" },
  [FEATURE_KEYS.ESSAY]: { backgroundColor: "#e0f2f1", textColor: "#00695c" },
  [FEATURE_KEYS.RESOURCE]: { backgroundColor: "#f3e5f5", textColor: "#7b1fa2" },
  [FEATURE_KEYS.EDITAL]: { backgroundColor: "#fff3e0", textColor: "#e65100" },
  [FEATURE_KEYS.INSCRICAO]: {
    backgroundColor: "#dfab1c20",
    textColor: "#dfab1c",
  },
} as const;

const MAIN_FEATURE_CLASS_MAP: Record<MainFeatureKey, string> = {
  [FEATURE_KEYS.EXAM]: "bg-success-soft shadow-lg",
  [FEATURE_KEYS.ESSAY]: "bg-primary-soft",
  [FEATURE_KEYS.RESOURCE]: "bg-purple-soft",
  [FEATURE_KEYS.EDITAL]: "bg-warning-soft",
  [FEATURE_KEYS.INSCRICAO]: "bg-yellow-soft",
} as const;

const MAIN_FEATURE_DESCRIPTION_MAP: Record<MainFeatureKey, string> = {
  [FEATURE_KEYS.EDITAL]: "Acesse os detalhes e regras do processo seletivo.",
  [FEATURE_KEYS.EXAM]:
    "Acesse as informações e confira sua classificação e notas.",
  [FEATURE_KEYS.ESSAY]: "Acesse a redação e veja suas redações e comentários.",
  [FEATURE_KEYS.RESOURCE]: "Solicite a revisão de alguma questão ou resultado.",
  [FEATURE_KEYS.INSCRICAO]: "Acesse os detalhes da sua inscrição.", // Description from PHP label
} as const;

// Ensure FEATURE_ROUTES also includes the new key if it's a main feature with a specific route
// Example: export const FEATURE_ROUTES = { ..., 'view-inscricao': '/inscricao' } as const;

export const getFeatureRoutePath = (
  featureKey: string,
  processId: number
): string => {
  const routeSuffix = FEATURE_ROUTES[featureKey as MainFeatureKey] || "";
  return `/process/${processId}${routeSuffix}`;
};

export const getFeatureIcon = (
  featureKey: string,
  iconName?: string
): React.ReactNode => {
  if (iconName) {
    const mappedIcon = getIcon(iconName);
    if (mappedIcon) return <Icon icon={mappedIcon} size={36} />;
  }
  const iconComponent =
    MAIN_FEATURE_ICON_MAP[featureKey as MainFeatureKey] || FaFileAlt;
  return <Icon icon={iconComponent} size={36} />;
};

export const getFeatureColors = (featureKey: string, color?: string) => {
  if (color?.startsWith("#")) {
    return { backgroundColor: `${color}20`, textColor: color };
  }
  return (
    MAIN_FEATURE_COLOR_MAP[featureKey as MainFeatureKey] || {
      backgroundColor: "#f5f5f5",
      textColor: "#666666",
    }
  );
};

export const getFeatureClassName = (featureKey: string): string => {
  return (
    MAIN_FEATURE_CLASS_MAP[featureKey as MainFeatureKey] || "bg-secondary-soft"
  );
};

export const getFeatureDescription = (
  featureKey: string,
  label: string
): string => {
  return (
    MAIN_FEATURE_DESCRIPTION_MAP[featureKey as MainFeatureKey] ||
    `Acesse ${label.toLowerCase()}.`
  );
};

export const createCardFromFeature = (
  feature: NonNullable<FullProcess["faculdadePermissions"]>[0]["features"][0],
  processId: number
): ProcessCardItem | null => {
  if (!isMainFeatureMapped(feature.feature_key)) return null;

  const colors = getFeatureColors(feature.feature_key, feature.color);
  return {
    id: feature.id,
    title: feature.label,
    description: getFeatureDescription(feature.feature_key, feature.label),
    routePath: getFeatureRoutePath(feature.feature_key, processId),
    icon: getFeatureIcon(feature.feature_key, feature.icon),
    backgroundColor: colors.backgroundColor,
    textColor: colors.textColor,
    className: getFeatureClassName(feature.feature_key),
  };
};

const isMainFeatureMapped = (featureKey: string): boolean => {
  const key = featureKey as MainFeatureKey;
  return !!(
    FEATURE_ROUTES[key] &&
    MAIN_FEATURE_ICON_MAP[key] &&
    MAIN_FEATURE_COLOR_MAP[key] &&
    MAIN_FEATURE_CLASS_MAP[key] &&
    MAIN_FEATURE_DESCRIPTION_MAP[key]
  );
};

export type CreateCardsOptions = {
  includeFeatures?: boolean;
  includeSubFeatures?: boolean;
  featureKeys?: string[];
  subFeatureKeys?: string[];
};

export const createCardsFromPermissions = (
  permissions: FullProcess["faculdadePermissions"] | undefined,
  processId: number,
  options: CreateCardsOptions = {}
): ProcessCardItem[] => {
  const {
    includeFeatures = true,
    includeSubFeatures = false,
    featureKeys,
    subFeatureKeys,
  } = options;
  if (!permissions?.length) return [];

  const cards: ProcessCardItem[] = [];

  permissions.forEach((permission) => {
    if (permission.enabled && permission.features) {
      permission.features.forEach((feature) => {
        if (feature.permissionEnabled) {
          if (
            includeFeatures &&
            (!featureKeys || featureKeys.includes(feature.feature_key))
          ) {
            const card = createCardFromFeature(feature, processId);
            if (card) cards.push(card);
          }

          if (includeSubFeatures && feature.subFeatures) {
            const { createCardFromSubFeature } = require("./subFeatureConfig");
            feature.subFeatures.forEach((subFeature) => {
              if (
                subFeature.permissionEnabled &&
                (!subFeatureKeys ||
                  subFeatureKeys.includes(subFeature.subFeatureKey))
              ) {
                const card = createCardFromSubFeature(subFeature, processId);
                if (card) cards.push(card);
              }
            });
          }
        }
      });
    }
  });

  return cards;
};

export const createCardsFromFeatures = (
  permissions: FullProcess["faculdadePermissions"] | undefined,
  processId: number
): ProcessCardItem[] => {
  return createCardsFromPermissions(permissions, processId, {
    includeFeatures: true,
  });
};

const hasValidCards = (cards: ProcessCardItem[]): boolean => {
  return cards.length > 0 && cards[0].id !== "no-permissions-warning";
};

const createFallbackResourceCard = (processId: number): ProcessCardItem => {
  const { getSubFeatureIcon } = require("./subFeatureConfig");
  return {
    id: "question-resource",
    title: "Recurso de Questões",
    description: "Solicite recurso para questões de um simulado.",
    routePath: `/process/${processId}/resource/question`,
    icon: getSubFeatureIcon("resource-question"),
    backgroundColor: "#fff8e1",
    textColor: "#ff6f00",
    className: "bg-warning-soft",
  };
};

export const createResourceCards = (
  permissions: FullProcess["faculdadePermissions"] | undefined,
  processId: number
): ProcessCardItem[] => {
  const { createCardsFromSubFeatures } = require("./subFeatureConfig");

  const validCards = [
    createCardsFromSubFeatures(permissions, processId, ["resource-question"]),
    createCardsFromPermissions(permissions, processId, {
      includeFeatures: true,
      includeSubFeatures: false,
      featureKeys: ["resource"],
    }),
  ].find(hasValidCards);

  return validCards ?? [createFallbackResourceCard(processId)];
};
